import styled from 'styled-components';
import type {ActivityEvent, PriorityChangeEvent} from '../types/activityEvent';

interface Props {
	event: ActivityEvent;
}

function formatTime(iso: string): string {
	return new Date(iso).toLocaleString();
}

const STATUS_LABELS: Record<string, string> = {
	open: 'Open',
	in_progress: 'In progress',
	waiting_on_customer: 'Waiting on customer',
	resolved: 'Resolved',
	closed: 'Closed',
};

const PRIORITY_LABELS: Record<string, string> = {
	p0: 'Critical',
	p1: 'High',
	p2: 'Medium',
	p3: 'Low',
};

function actorLabel(event: {actor_id: string | null; actor_name: string | null}): string {
	if (!event.actor_id) return 'System';
	return event.actor_name ?? 'Unknown';
}

function statusLabel(raw: string): string {
	return STATUS_LABELS[raw] ?? raw;
}

function priorityLabel(raw: string): string {
	return PRIORITY_LABELS[raw] ?? raw;
}

// EventPill colours were inline `var(--token, #fallback)` references whose tokens
// are not part of the ticketing theme; the rendered values are the hex fallbacks,
// preserved here as named variants.
type Variant = 'neutral' | 'orange' | 'amber' | 'red';

const PILL_COLORS: Record<Variant, {bg: string; text: string}> = {
	neutral: {bg: '#f5f3f9', text: '#6b6d7b'},
	orange: {bg: '#fff7ed', text: '#c2410c'},
	amber: {bg: '#fffbeb', text: '#b45309'},
	red: {bg: '#fef2f2', text: '#b91c1c'},
};

const Row = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 8px;
	padding: 4px 0;
`;
const Divider = styled.span`
	height: 1px;
	flex: 1;
	background: #e2e2ea;
`;
const Pill = styled.span<{$variant: Variant}>`
	border-radius: ${p => p.theme.ticketing.radius.full};
	padding: 4px 12px;
	font: ${p => p.theme.ticketing.font.body3};
	font-weight: 500;
	background: ${p => PILL_COLORS[p.$variant].bg};
	color: ${p => PILL_COLORS[p.$variant].text};
`;
const Time = styled.span`
	font: ${p => p.theme.ticketing.font.body3};
	color: #6b6d7b;
`;
const Emphasis = styled.span`
	font-weight: 600;
	color: #232334;
`;
const Main = styled.span`
	color: #232334;
`;

function PillRow({id, variant, time, children}: {id: string; variant: Variant; time: string; children: React.ReactNode}) {
	return (
		<Row id={id}>
			<Divider />
			<Pill data-testid="event-pill" $variant={variant}>
				{children}
			</Pill>
			<Time>{time}</Time>
			<Divider />
		</Row>
	);
}

export function EventPill({event}: Props) {
	const actor = actorLabel(event as {actor_id: string | null; actor_name: string | null});
	const time = formatTime(event.created_at);

	switch (event.event_type) {
		case 'status_change': {
			const p = (event as {payload: unknown}).payload as {from?: string; to?: string} | null;
			const from = p?.from ? statusLabel(p.from) : '?';
			const to = p?.to ? statusLabel(p.to) : '?';
			return (
				<PillRow id={event.id} variant="neutral" time={time}>
					<Emphasis>{actor}</Emphasis> changed status:{' '}
					<Main>
						{from} → {to}
					</Main>
				</PillRow>
			);
		}

		case 'priority_change': {
			const e = event as PriorityChangeEvent;
			const p = e.payload as {from?: string; to?: string} | null;
			const from = p?.from ? priorityLabel(p.from) : '?';
			const to = p?.to ? priorityLabel(p.to) : '?';
			return (
				<PillRow id={event.id} variant="orange" time={time}>
					<Emphasis>{actor}</Emphasis> changed priority:{' '}
					<span>
						{from} → {to}
					</span>
				</PillRow>
			);
		}

		case 'assignment': {
			const p = (event as {payload: unknown}).payload as {
				from?: string | null;
				to?: string | null;
			} | null;
			let description: string;
			if (p?.to && !p?.from) {
				description = 'assigned ticket';
			} else if (!p?.to && p?.from) {
				description = 'unassigned ticket';
			} else {
				description = 'reassigned ticket';
			}
			return (
				<PillRow id={event.id} variant="neutral" time={time}>
					<Emphasis>{actor}</Emphasis> {description}
				</PillRow>
			);
		}

		case 'reopened_by_customer':
			return (
				<PillRow id={event.id} variant="amber" time={time}>
					Customer reopened ticket
				</PillRow>
			);

		case 'auto_closed':
			return (
				<PillRow id={event.id} variant="neutral" time={time}>
					<Emphasis>System</Emphasis> auto-closed ticket
				</PillRow>
			);

		case 'sla.response_breached':
			return (
				<PillRow id={event.id} variant="red" time={time}>
					<Emphasis>System</Emphasis> SLA breached (Response)
				</PillRow>
			);

		case 'sla.resolution_breached':
			return (
				<PillRow id={event.id} variant="red" time={time}>
					<Emphasis>System</Emphasis> SLA breached (Resolution)
				</PillRow>
			);

		case 'ingest_failed':
			return (
				<PillRow id={event.id} variant="red" time={time}>
					<Emphasis>System</Emphasis> email ingest failed
				</PillRow>
			);

		case 'time_entry': {
			const p = (event as {payload: unknown}).payload as {
				from?: string;
				to?: string;
			} | null;
			const duration = p?.to ? `${p.to}h` : null;
			return (
				<PillRow id={event.id} variant="neutral" time={time}>
					<Emphasis>{actor}</Emphasis> logged time{duration ? `: ${duration}` : ''}
				</PillRow>
			);
		}

		case 'comment':
		case 'note':
		case 'merge':
			return (
				<PillRow id={event.id} variant="neutral" time={time}>
					<Emphasis>{actor}</Emphasis> {event.event_type.replace(/_/g, ' ')}
				</PillRow>
			);

		case 'ai_suggested_action_accepted': {
			// The accept route (ACC-296) stores the created task's companyTaskId in
			// payload.to so we can deep-link to /T<companyTaskId>.
			const p = (event as {payload: unknown}).payload as {to?: string} | null;
			const taskNumber = p?.to;
			return (
				<PillRow id={event.id} variant="neutral" time={time}>
					{actor !== 'System' && (
						<>
							<Emphasis>{actor}</Emphasis>{' '}
						</>
					)}
					accepted an AI-suggested task{taskNumber ? ' ' : ''}
					{taskNumber && <a href={`/T${taskNumber}`}>View task</a>}
				</PillRow>
			);
		}

		default:
			return (
				<PillRow id={event.id} variant="neutral" time={time}>
					{actor !== 'System' && (
						<>
							<Emphasis>{actor}</Emphasis>{' '}
						</>
					)}
					{event.event_type.replace(/_/g, ' ')}
				</PillRow>
			);
	}
}
